/**
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You
 * may not use this file except in compliance with the License. A copy of
 * the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is
 * distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the License for the specific
 * language governing permissions and limitations under the License.
 */
import { IdleState, BusyState, busyState, TokensFromSignIn } from './model';
import { authProxyApi } from '@verifime/auth-proxy-definition';
import { logProxyError } from './auth-proxy-utils';
import { defaultTokensCb } from 'amazon-cognito-passwordless-auth/common';
import {
  parseJwtPayload,
  throwIfNot2xx,
  bufferFromBase64Url,
  bufferToBase64Url,
} from 'amazon-cognito-passwordless-auth/util';
//import { configure } from "./config.js";
import { retrieveTokens } from 'amazon-cognito-passwordless-auth/storage';
import { CognitoIdTokenPayload } from 'amazon-cognito-passwordless-auth/jwt-model';

import { Passwordless } from 'amazon-cognito-passwordless-auth';

const configure = Passwordless.configure;

export interface StoredCredential {
  credentialId: string;
  friendlyName: string;
  createdAt: Date;
  lastSignIn?: Date;
  signCount: number;
  transports?: AuthenticatorTransport[];
}

type AuthenticatorAttestationResponseWithOptionalMembers = AuthenticatorAttestationResponse & {
  getTransports?: () => '' | string[];
  getAuthenticatorData?: () => unknown;
  getPublicKey?: () => unknown;
  getPublicKeyAlgorithm?: () => unknown;
};

export async function fido2CreateCredential({
  friendlyName,
}: {
  friendlyName: string | (() => string | Promise<string>);
}) {
  const { debug, fido2: { attestation, authenticatorSelection, extensions, rp, timeout } = {} } =
    configure();
  const publicKeyOptions = await fido2StartCreateCredential();
  const publicKey: CredentialCreationOptions['publicKey'] = {
    ...publicKeyOptions,
    rp: {
      name: rp?.name ?? publicKeyOptions.rp.name,
      id: rp?.id ?? publicKeyOptions.rp.id,
    },
    attestation,
    authenticatorSelection,
    extensions,
    timeout,
    challenge: bufferFromBase64Url(publicKeyOptions.challenge),
    user: {
      ...publicKeyOptions.user,
      id: Uint8Array.from(publicKeyOptions.user.id, (c) => c.charCodeAt(0)),
    },
    excludeCredentials: publicKeyOptions.excludeCredentials.map((credential) => ({
      ...credential,
      id: bufferFromBase64Url(credential.id),
    })),
  };
  debug?.('Assembled public key options:', publicKey);
  const credential = await navigator.credentials.create({
    publicKey,
  });
  if (!credential) {
    throw new Error('empty credential');
  }
  if (
    !(credential instanceof PublicKeyCredential) ||
    !(credential.response instanceof AuthenticatorAttestationResponse)
  ) {
    throw new Error('credential.response is not an instance of AuthenticatorAttestationResponse');
  }
  const response: AuthenticatorAttestationResponseWithOptionalMembers = credential.response;
  //   debug?.('Created credential:', {
  //     credential,
  //     getTransports: response.getTransports?.(),
  //     getAuthenticatorData: response.getAuthenticatorData?.(),
  //     getPublicKey: response.getPublicKey?.(),
  //     getPublicKeyAlgorithm: response.getPublicKeyAlgorithm?.(),
  //   });
  const resolvedFriendlyName =
    typeof friendlyName === 'string' ? friendlyName : await friendlyName();
  return fido2CompleteCreateCredential({
    credential: credential,
    friendlyName: resolvedFriendlyName,
  });
}

interface StartCreateCredentialResponse {
  challenge: string;
  attestation: 'none';
  rp: { name: string; id?: string };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: { type: 'public-key'; alg: -7 | -257 }[];
  authenticatorSelection: { userVerification: UserVerificationRequirement };
  timeout: number;
  excludeCredentials: {
    id: string;
    type: 'public-key';
  }[];
}

export interface ParsedCredential {
  clientDataJSON_B64: string;
  attestationObjectB64: string;
  transports?: string[]; // Should be: "usb" | "nfc" | "ble" | "internal" | "hybrid"
}

export async function fido2StartCreateCredential() {
  const { fido2 } = configure();
  if (!fido2) {
    throw new Error('Missing Fido2 config');
  }
  const { idToken } = (await retrieveTokens()) ?? {};
  if (!idToken) {
    throw new Error('No JWT to invoke Fido2 API with');
  }
  const url = new URL(
    `/register-authenticator/start?rpId=${fido2.rp?.id ?? window.location.hostname}`,
    fido2.baseUrl,
  );
  const method = 'POST';
  return fetch(url, {
    method,
    headers: {
      accept: 'application/json, text/javascript',
      'content-type': 'application/json; charset=UTF-8',
      authorization: `Bearer ${idToken}`,
    },
  })
    .then(throwIfNot2xx)
    .then((res) => res.json() as Promise<StartCreateCredentialResponse>);
}

export async function fido2CompleteCreateCredential({
  credential,
  friendlyName,
}: {
  credential: PublicKeyCredential | ParsedCredential;
  friendlyName: string;
}) {
  const { fido2 } = configure();
  if (!fido2) {
    throw new Error('Missing Fido2 config');
  }
  const { idToken } = (await retrieveTokens()) ?? {};
  if (!idToken) {
    throw new Error('No JWT to invoke Fido2 API with');
  }
  const url = new URL('/register-authenticator/complete', fido2.baseUrl);
  const method = 'POST';
  const parsedCredential =
    'response' in credential
      ? await parseAuthenticatorAttestationResponse(
          credential.response as AuthenticatorAttestationResponseWithOptionalMembers,
        )
      : credential;

  return fetch(url, {
    body: JSON.stringify({
      ...parsedCredential,
      friendlyName,
    }),
    method,
    headers: {
      accept: 'application/json, text/javascript',
      'content-type': 'application/json; charset=UTF-8',
      authorization: `Bearer ${idToken}`,
    },
  })
    .then(throwIfNot2xx)
    .then(
      (res) =>
        res.json() as Promise<{
          friendlyName: string;
          credentialId: string;
          createdAt: string;
          signCount: number;
        }>,
    )
    .then(
      (res) =>
        ({
          ...res,
          createdAt: new Date(res.createdAt),
        }) as StoredCredential,
    );
}

export async function fido2ListCredentials() {
  const { fido2 } = configure();
  if (!fido2) {
    throw new Error('Missing Fido2 config');
  }
  const tokens = await retrieveTokens();
  if (!tokens?.idToken) {
    throw new Error('No JWT to invoke Fido2 API with');
  }
  const url = new URL(
    `/authenticators/list?rpId=${fido2.rp?.id ?? window.location.hostname}`,
    fido2.baseUrl,
  );
  return fetch(url, {
    method: 'POST',
    headers: {
      accept: 'application/json, text/javascript',
      'content-type': 'application/json; charset=UTF-8',
      authorization: `Bearer ${tokens.idToken}`,
    },
  })
    .then(throwIfNot2xx)
    .then(
      (res) =>
        res.json() as Promise<{
          authenticators: {
            friendlyName: string;
            credentialId: string;
            createdAt: string;
            signCount: number;
            lastSignIn?: string;
          }[];
        }>,
    )
    .then(({ authenticators }) => ({
      authenticators: authenticators.map((authenticator) => ({
        ...authenticator,
        createdAt: new Date(authenticator.createdAt),
        lastSignIn:
          authenticator.lastSignIn !== undefined
            ? new Date(authenticator.lastSignIn)
            : (authenticator.lastSignIn as undefined),
      })),
    }));
}

export async function fido2DeleteCredential({ credentialId }: { credentialId: string }) {
  const { fido2 } = configure();
  if (!fido2) {
    throw new Error('Missing Fido2 config');
  }
  const tokens = await retrieveTokens();
  if (!tokens?.idToken) {
    throw new Error('No JWT to invoke Fido2 API with');
  }
  const url = new URL('/authenticators/delete', fido2.baseUrl);
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ credentialId }),
    headers: {
      accept: 'application/json, text/javascript',
      'content-type': 'application/json; charset=UTF-8',
      authorization: `Bearer ${tokens.idToken}`,
    },
  }).then(throwIfNot2xx);
}

export async function fido2UpdateCredential({
  credentialId,
  friendlyName,
}: {
  credentialId: string;
  friendlyName: string;
}) {
  const { fido2 } = configure();
  if (!fido2) {
    throw new Error('Missing Fido2 config');
  }
  const tokens = await retrieveTokens();
  if (!tokens?.idToken) {
    throw new Error('No JWT to invoke Fido2 API with');
  }
  const url = new URL('/authenticators/update', fido2.baseUrl);
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ credentialId, friendlyName }),
    headers: {
      accept: 'application/json, text/javascript',
      'content-type': 'application/json; charset=UTF-8',
      authorization: `Bearer ${tokens.idToken}`,
    },
  }).then(throwIfNot2xx);
}

interface Fido2Options {
  challenge: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  relyingPartyId?: string;
  credentials?: { id: string; transports?: AuthenticatorTransport[] }[];
}

async function fido2getCredential({
  relyingPartyId,
  challenge,
  credentials,
  timeout,
  userVerification,
}: Fido2Options) {
  const { debug, fido2: { extensions } = {} } = configure();
  const publicKey: CredentialRequestOptions['publicKey'] = {
    challenge: bufferFromBase64Url(challenge),
    allowCredentials: credentials?.map((credential) => ({
      id: bufferFromBase64Url(credential.id),
      transports: credential.transports,
      type: 'public-key' as const,
    })),
    timeout,
    userVerification,
    rpId: relyingPartyId,
    extensions,
  };
  debug?.('Assembled public key options:', publicKey);
  const credential = await navigator.credentials.get({
    publicKey,
  });
  if (!credential) {
    throw new Error(`Failed to get credential`);
  }
  if (
    !(credential instanceof PublicKeyCredential) ||
    !(credential.response instanceof AuthenticatorAssertionResponse)
  ) {
    throw new Error('credential.response is not an instance of AuthenticatorAssertionResponse');
  }
  debug?.('Credential:', credential);
  return parseAuthenticatorAssertionResponse(credential.rawId, credential.response);
}

const parseAuthenticatorAttestationResponse = async (
  response: AuthenticatorAttestationResponseWithOptionalMembers,
) => {
  const [attestationObjectB64, clientDataJSON_B64] = await Promise.all([
    bufferToBase64Url(response.attestationObject),
    bufferToBase64Url(response.clientDataJSON),
  ]);
  const transports = (response.getTransports?.() || []).filter((transport) =>
    ['ble', 'hybrid', 'internal', 'nfc', 'usb'].includes(transport),
  );
  return {
    attestationObjectB64,
    clientDataJSON_B64,
    transports: transports.length ? transports : undefined,
  };
};

const parseAuthenticatorAssertionResponse = async (
  rawId: ArrayBuffer,
  response: AuthenticatorAssertionResponse,
) => {
  const [credentialIdB64, authenticatorDataB64, clientDataJSON_B64, signatureB64, userHandleB64] =
    await Promise.all([
      bufferToBase64Url(rawId),
      bufferToBase64Url(response.authenticatorData),
      bufferToBase64Url(response.clientDataJSON),
      bufferToBase64Url(response.signature),
      response.userHandle && response.userHandle.byteLength > 0
        ? bufferToBase64Url(response.userHandle)
        : null,
    ]);
  return {
    credentialIdB64,
    authenticatorDataB64,
    clientDataJSON_B64,
    signatureB64,
    userHandleB64,
  };
};

function mergeCredentials(
  serverCredentials: Fido2Options['credentials'],
  clientCredentials?: { id: string; transports?: AuthenticatorTransport[] }[],
): Fido2Options['credentials'] {
  const server = serverCredentials ?? [];
  return server.concat(
    clientCredentials?.filter(
      (cred) => !server.find((sc) => cred.id === sc.id),
    ) ?? [],
  );
}

export function authenticateWithFido2({
  username,
  credentials,
  tokensCb,
  statusCb,
  currentStatus,
  credentialGetter = fido2getCredential,
}: {
  /**
   * Username, or alias (e-mail, phone number)
   */
  username: string;
  credentials?: { id: string; transports?: AuthenticatorTransport[] }[];
  tokensCb?: (tokens: TokensFromSignIn) => void | Promise<void>;
  statusCb?: (status: BusyState | IdleState) => void;
  currentStatus?: BusyState | IdleState;
  credentialGetter?: typeof fido2getCredential;
}) {
  if (currentStatus && busyState.includes(currentStatus as BusyState)) {
    throw new Error(`Can't sign in while in status ${currentStatus}`);
  }
  const abort = new AbortController();
  const signedIn = (async (): Promise<TokensFromSignIn> => {
    const { debug } = configure();
    statusCb?.('STARTING_SIGN_IN_WITH_FIDO2');
    try {
      // signInMethod metadata is handled server-side by the proxy. Empty
      // string is not a valid portal ID; treat as absent.
      const portal = process.env.NEXT_PUBLIC_AUTH_PORTAL_ID || undefined;

      debug?.('Invoking auth proxy initiateAuth ...');
      const initResponse = await authProxyApi.postAuthinitiate(
        { email: username, method: 'FIDO2', portal },
        { signal: abort.signal },
      );
      debug?.('Response from auth proxy initiateAuth:', initResponse);

      if (initResponse.challengeType !== 'FIDO2' || !initResponse.fido2Options) {
        throw new Error('Expected FIDO2 challenge from auth proxy');
      }
      if (!initResponse.fido2Options.challenge) {
        throw new Error('Auth proxy returned FIDO2 response without a challenge string');
      }

      // Local rp.id config takes precedence over the proxy's relyingPartyId.
      // When neither is set, relyingPartyId is undefined and WebAuthn defaults
      // to window.location.hostname (correct for localhost dev).
      const { fido2: { rp } = {} } = configure();

      const fido2options: Fido2Options = {
        challenge: initResponse.fido2Options.challenge,
        relyingPartyId: rp?.id,
        // Cast: generated schema types transports as string[] rather than AuthenticatorTransport[]
        credentials: initResponse.fido2Options.credentials as
          | { id: string; transports?: AuthenticatorTransport[] }[]
          | undefined,
        timeout: initResponse.fido2Options.timeout,
        userVerification: initResponse.fido2Options.userVerification as
          | UserVerificationRequirement
          | undefined,
      };

      fido2options.credentials = mergeCredentials(fido2options.credentials, credentials);

      debug?.('FIDO2 options from auth proxy:', fido2options);
      const fido2credential = await credentialGetter(fido2options);
      statusCb?.('COMPLETING_SIGN_IN_WITH_FIDO2');

      debug?.('Invoking auth proxy respondToChallenge ...');
      const authResult = await authProxyApi.postAuthrespond(
        {
          session: initResponse.session,
          method: 'FIDO2',
          portal,
          // Cast needed: generated Zod schema types answer as Partial<{}> with passthrough,
          // but the concrete credential object satisfies it structurally
          answer: fido2credential as Record<string, unknown>,
        },
        { signal: abort.signal },
      );
      debug?.('Response from auth proxy respondToChallenge:', authResult);

      const tokens: TokensFromSignIn = {
        accessToken: authResult.accessToken,
        idToken: authResult.idToken,
        refreshToken: authResult.refreshToken,
        expireAt: new Date(Date.now() + authResult.expiresIn * 1000),
        username: parseJwtPayload<CognitoIdTokenPayload>(authResult.idToken)['cognito:username'],
      };

      tokensCb ? await tokensCb(tokens) : await defaultTokensCb({ tokens, abort: abort.signal });
      statusCb?.('SIGNED_IN_WITH_FIDO2');
      return tokens;
    } catch (err) {
      logProxyError('FIDO2 authentication failed', err);
      statusCb?.('FIDO2_SIGNIN_FAILED');
      throw err;
    }
  })();
  return {
    signedIn,
    abort: () => abort.abort(),
  };
}
